import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const authApi = createApi({
    reducerPath: 'authApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/auth`,
        prepareHeaders: (headers, { getState }) => {
            // Type assertion for RootState will be needed or we can just access it loosely if circular dependency is an issue
            // Ideally importing RootState from store.ts, but store.ts imports this.
            // So we can use a simpler approach or just check localStorage as a fallback
            const token = (getState() as any).auth.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    endpoints: (builder) => ({
        login: builder.mutation({
            query: (credentials) => ({
                url: '/login',
                method: 'POST',
                body: credentials,
            }),
        }),
        signup: builder.mutation<SignupResponse, SignupRequest>({
            query: (userData) => ({
                url: '/signup',
                method: 'POST',
                body: userData,
            }),
        }),
        getUserInfo: builder.query({
            query: () => '/info',
        }),
        forgotPassword: builder.mutation({
            query: (data) => ({
                url: '/forgot-password',
                method: 'POST',
                body: data,
            }),
        }),
        resetPassword: builder.mutation({
            query: ({ token, password }) => ({
                url: `/reset-password/${token}`,
                method: 'POST',
                body: { password },
            }),
        }),
        logout: builder.mutation({
            query: () => ({
                url: '/logout',
                method: 'POST',
            }),
        }),
        deleteAccount: builder.mutation({
            query: () => ({
                url: '/delete-account',
                method: 'DELETE',
            }),
        }),
        getReferralInfo: builder.query<ReferralInfoResponse, void>({
            query: () => '/referral-info',
        }),
        applyReferralCode: builder.mutation<{ success: boolean; message: string }, { referralCode: string }>({
            query: (data) => ({
                url: '/apply-referral',
                method: 'POST',
                body: data,
            }),
        }),
        verifyReferralCode: builder.mutation<VerifyReferralResponse, { referralCode: string }>({
            query: (data) => ({
                url: '/verify-referral',
                method: 'POST',
                body: data,
            }),
        }),
        inviteFriends: builder.mutation<InviteFriendsResponse, { emails: string[] }>({
            query: (data) => ({
                url: '/invite',
                method: 'POST',
                body: data,
            }),
        }),
        googleOneTap: builder.mutation<{ user: any; token: string }, { credential: string }>({
            query: (data) => ({
                url: '/google/one-tap',
                method: 'POST',
                body: data,
            }),
        }),
        getTransactions: builder.query<GetTransactionsResponse, { page?: number; limit?: number }>({
            query: ({ page = 1, limit = 10 }) => `/transactions?page=${page}&limit=${limit}`,
        }),
        getInvoice: builder.query<Blob, { transactionId: string }>({
            query: ({ transactionId }) => ({
                url: `/transactions/${transactionId}/invoice`,
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

// Types for signup
export interface SignupRequest {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    referralCode?: string;
}

export interface SignupResponse {
    success: boolean;
    user: {
        _id: string;
        firstName: string;
        lastName?: string;
        email: string;
        decks?: number;
    };
    token: string;
    message?: string;
}

// Types for referral
export interface ReferralHistory {
    _id: string;
    refereeId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        createdAt: string;
    };
    status: 'PENDING' | 'COMPLETED';
    decksReferrer: number;
    decksReferee: number;
    completedAt: string | null;
    createdAt: string;
}

export interface ReferralInfoResponse {
    success: boolean;
    referralCode: string;
    decks: number;
    referrals: ReferralHistory[];
}

export interface VerifyReferralResponse {
    success: boolean;
    user?: {
        firstName: string;
        lastName?: string;
    };
    message?: string;
}

export interface InviteFriendsResponse {
    success: boolean;
    message: string;
    sent: number;
    failed: number;
}

export interface Transaction {
    _id: string;
    userId: string;
    deckId?: string;
    amount: number;
    currency: string;
    status: 'created' | 'captured' | 'failed' | 'refunded';
    purchaseType: string;
    razorPayOrderId: string;
    razorPayPaymentId?: string;
    paymentMethod?: string;
    paymentDate?: string;
    invoiceNumber?: string;
    invoiceUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface GetTransactionsResponse {
    success: boolean;
    message: string;
    transactions: Transaction[];
    total?: number;
}

export const {
    useLoginMutation,
    useSignupMutation,
    useGetUserInfoQuery,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useLogoutMutation,
    useDeleteAccountMutation,
    useGetReferralInfoQuery,
    useApplyReferralCodeMutation,
    useVerifyReferralCodeMutation,
    useInviteFriendsMutation,
    useGoogleOneTapMutation,
    useGetTransactionsQuery,
    useLazyGetInvoiceQuery,
} = authApi;
